import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { PosSaleListItem } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { useSession } from "../../session";
import { nfceInFlight, nfceLabel } from "./labels";

type Page = { data: PosSaleListItem[]; nextCursor: string | null };

const when = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

/** Vendas › Vendas do PDV (T3.1, RF-VEN-05): vendas da frente de caixa com a NFC-e, por data e situação. */
export function PosSalesListPage(): ReactElement {
  const { companyId, api } = useSession();
  const [status, setStatus] = useState<"" | "completed" | "canceled">("");
  const list = useInfiniteQuery({
    queryKey: ["pos-sales", companyId, status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<Page>(
        `/pos/sales?${new URLSearchParams({
          ...(status ? { status } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.data.some((d) => nfceInFlight(d.nfce))) ? 3000 : false,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <>
      <ContentHeader
        title="Vendas do PDV"
        breadcrumb={["Vendas", "Vendas do PDV"]}
        actions={
          <div className="d-flex gap-2">
            <Link to="/vendas/pdv/caixas" className="btn btn-outline-secondary">
              Caixas
            </Link>
            <Link to="/vendas/pdv/configuracao" className="btn btn-outline-secondary">
              Configuração
            </Link>
            <a href="/pdv" className="btn btn-primary">
              Abrir o PDV
            </a>
          </div>
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-3">
            <label className="visually-hidden" htmlFor="pos-sales-status">
              Situação
            </label>
            <select
              id="pos-sales-status"
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="">Todas as situações</option>
              <option value="completed">Concluídas</option>
              <option value="canceled">Canceladas</option>
            </select>
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhuma venda no PDV"
            description="As vendas da frente de caixa (online e sem conexão) aparecem aqui com a NFC-e."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Vendas do PDV">
                <thead>
                  <tr>
                    <th scope="col">Venda</th>
                    <th scope="col">Data</th>
                    <th scope="col">Terminal</th>
                    <th scope="col">Cliente</th>
                    <th scope="col" className="text-end">
                      Total
                    </th>
                    <th scope="col">NFC-e</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const nf = nfceLabel(r.nfce);
                    return (
                      <tr key={r.id}>
                        <td>
                          <Link to="/vendas/pdv/$id" params={{ id: r.id }}>
                            nº {r.number}
                          </Link>
                          {r.offline && <span className="badge text-bg-warning ms-2">Sem conexão</span>}
                        </td>
                        <td className="text-nowrap">{when(r.soldAt)}</td>
                        <td>{r.terminalName}</td>
                        <td>{r.contactName}</td>
                        <td className="text-end">
                          {formatBRL(r.totalCents)}
                          {r.divergenceCents !== 0 && (
                            <span
                              className="text-danger small d-block"
                              title="Diferença do total do terminal"
                            >
                              divergência {formatBRL(r.divergenceCents)}
                            </span>
                          )}
                        </td>
                        <td>
                          <StatusBadge tone={nf.tone}>{nf.text}</StatusBadge>
                          {r.nfce?.number && (
                            <span className="small text-muted ms-1">nº {r.nfce.number}</span>
                          )}
                        </td>
                        <td>
                          <StatusBadge tone={r.status === "completed" ? "success" : "secondary"}>
                            {r.status === "completed" ? "Concluída" : "Cancelada"}
                          </StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {list.hasNextPage && (
              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
